import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Empty from "antd/es/empty";
import { Modal, Table } from "../../components";
import FiledCopiesDocuments from "../../components/FiledCopiesDocuments/FiledCopiesDocuments";
import CompleteServiceFiledCopiesModal from "../../components/CompleteServiceFiledCopiesModal/CompleteServiceFiledCopiesModal";
import { CLIENT_ACTION } from "../../redux/action";
import { dateTimeFormatter, Toaster } from "../../utils/common";
import { navigateToClientDetails } from "../../utils/clientDetailsSession";
import ClientServiceRequestForm from "../../components/ClientServiceRequestForm/ClientServiceRequestForm";
import {
  formatServiceStatus,
  getServiceBusinessName,
  getServiceCategoryLabel,
  getServiceClientBusinessId,
  normalizeServiceStatus,
  SERVICE_COMPLETED_STATUS,
} from "../../constants/clientServiceOptions";
import {
  normalizeClientDetailCategory,
  recordMatchesClientCategory,
} from "../../utils/clientDetailsCategory";
import "./pending_services.scss";

const BASE_TABLE_HEADERS = [
  { id: "sno", label: "S.No" },
  { id: "client_name", label: "Client Name" },
  { id: "manager_name", label: "Manager" },
  { id: "service_name", label: "Service Name" },
  { id: "service_category", label: "Category" },
  { id: "tax_year", label: "Tax Year" },
  { id: "service_frequency", label: "Service Frequency" },
  { id: "status", label: "Status" },
  { id: "start_date", label: "Start Date" },
  { id: "updated_at", label: "Last Updated" },
];

const CATEGORY_TABS = [
  { key: "individual", label: "Individual" },
  { key: "business", label: "Business" },
];

const STATUS_TABS = [
  { key: "ongoing", label: "Ongoing" },
  { key: "closed", label: "Closed" },
];

const looksLikeUuid = (value) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(value || "").trim(),
  );

const pickDisplayName = (...candidates) => {
  for (const value of candidates) {
    const text = String(value || "").trim();
    if (!text || looksLikeUuid(text)) continue;
    return text;
  }
  return "";
};

const getClientDisplayName = (row) => {
  const nested = row?.client || row?.Client || {};
  const fromParts = [
    row?.first_name || row?.firstName || nested?.first_name || nested?.firstName,
    row?.last_name || row?.lastName || nested?.last_name || nested?.lastName,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    pickDisplayName(
      row?.client_name,
      row?.clientName,
      nested?.client_name,
      nested?.clientName,
      nested?.name,
      fromParts,
      row?.full_name,
      row?.fullName,
      row?.name,
      nested?.email,
      row?.email,
    ) || "—"
  );
};

const getManagerDisplayName = (row) => {
  const nested = row?.client || row?.Client || {};
  const manager = row?.manager || nested?.manager || {};
  const fromParts = [
    row?.manager_first_name ||
      row?.managerFirstName ||
      manager?.first_name ||
      manager?.firstName,
    row?.manager_last_name ||
      row?.managerLastName ||
      manager?.last_name ||
      manager?.lastName,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    pickDisplayName(
      row?.manager_name,
      row?.managerName,
      row?.sales_name,
      row?.salesName,
      row?.assigned_manager_name,
      row?.assignedManagerName,
      row?.client_manager_name,
      row?.clientManagerName,
      nested?.manager_name,
      nested?.managerName,
      nested?.sales_name,
      nested?.salesName,
      manager?.name,
      fromParts,
      typeof manager === "string" ? manager : "",
      // Avoid showing raw UUID ids as the manager label.
      row?.sales,
      nested?.sales,
    ) || "—"
  );
};

const formatStartDate = (row) => {
  const raw = row?.start_date || row?.startDate;
  if (!raw) return "—";
  const text = String(raw);
  return text.length >= 10 ? text.slice(0, 10) : text;
};

const ServicesPage = () => {
  const navigate = useNavigate();
  const [categoryTab, setCategoryTab] = useState("individual");
  const [statusTab, setStatusTab] = useState("ongoing");
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editClientId, setEditClientId] = useState("");
  const [editServiceId, setEditServiceId] = useState("");
  const [editInitialValues, setEditInitialValues] = useState(null);
  const [completeForm, setCompleteForm] = useState(null);

  const resetEditState = () => {
    setEditModalOpen(false);
    setEditSubmitting(false);
    setEditClientId("");
    setEditServiceId("");
    setEditInitialValues(null);
    setCompleteForm(null);
  };

  const loadServices = useCallback(async (status, category) => {
    setLoading(true);
    setRows([]);
    setTotalCount(0);
    try {
      const result = await CLIENT_ACTION.getDashboardPendingServices({
        status,
        service_category: category,
      });
      const services = Array.isArray(result?.services) ? result.services : [];
      // Client-side fallback if backend ignores service_category.
      const filtered = services.filter((row) =>
        recordMatchesClientCategory(
          {
            ...row,
            service_category:
              row?.service_category || row?.serviceCategory || row?.category,
          },
          category,
        ),
      );
      setRows(filtered);
      setTotalCount(filtered.length);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadServices(statusTab, categoryTab);
  }, [loadServices, statusTab, categoryTab]);

  const tableData = useMemo(
    () =>
      rows.map((row, index) => {
        const categoryKey = normalizeClientDetailCategory(
          row?.service_category || row?.serviceCategory || row?.category,
        );
        return {
          sno: index + 1,
          client_name: getClientDisplayName(row),
          manager_name: getManagerDisplayName(row),
          service_name: row?.service_name || row?.serviceName || "—",
          ...(categoryKey === "business"
            ? { business_name: getServiceBusinessName(row) }
            : {}),
          service_category: getServiceCategoryLabel(categoryKey),
          tax_year: row?.tax_year || row?.taxYear || "—",
          service_frequency:
            row?.service_frequency || row?.serviceFrequency || "—",
          status: formatServiceStatus(row?.status),
          ...(statusTab === "closed"
            ? {
                filed_copies: (
                  <FiledCopiesDocuments
                    documents={row?.filed_copies || row?.filedCopies}
                  />
                ),
              }
            : {}),
          start_date: formatStartDate(row),
          updated_at: dateTimeFormatter(
            row?.updatedAt || row?.updated_at || row?.last_updated_at,
          ),
          client_id: row?.client_id || row?.clientId || row?.user_id || "",
          client_service_id:
            row?.client_service_id ??
            row?.clientServiceId ??
            row?.service_id ??
            row?.serviceId ??
            "",
          client_business_id: getServiceClientBusinessId(row),
          enabledActions: {
            edit: String(formatServiceStatus(row?.status)).toLowerCase() !==
              SERVICE_COMPLETED_STATUS.toLowerCase(),
          },
          category_key: categoryKey,
        };
      }),
    [rows, statusTab],
  );

  const emptyLabel =
    statusTab === "ongoing"
      ? `No ongoing ${categoryTab} services`
      : `No closed ${categoryTab} services`;

  const normalizedCategory = normalizeClientDetailCategory(categoryTab);
  const categoryLabel = getServiceCategoryLabel(normalizedCategory);
  const tableHeaders = useMemo(() => {
    const headers = [...BASE_TABLE_HEADERS];
    if (normalizedCategory === "business") {
      headers.splice(4, 0, { id: "business_name", label: "Business" });
    }
    const statusIndex = headers.findIndex((header) => header.id === "status");
    if (statusTab === "closed") {
      headers.splice(statusIndex + 1, 0, {
        id: "filed_copies",
        label: "Filed Copies",
      });
    } else {
      headers.push({ id: "", label: "Action" });
    }
    return headers;
  }, [normalizedCategory, statusTab]);

  return (
    <div className="pending-services-page">
      <div className="client-details-category-switch">
        <div
          className="client-details-category-tabs"
          role="tablist"
          aria-label="Service category"
        >
          {CATEGORY_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={categoryTab === tab.key}
              className={`client-details-category-tab${
                categoryTab === tab.key ? " is-active" : ""
              }`}
              onClick={() => setCategoryTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="pending-services-card">
        <div className="pending-services-header">
          <div className="pending-services-title-wrapper">
            <p className="pending-services-subtitle">
              {loading
                ? ""
                : `${totalCount} service${totalCount !== 1 ? "s" : ""}`}
            </p>
          </div>
        </div>

        <div className="services-tabs">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`services-tab-btn${statusTab === tab.key ? " active" : ""}`}
              onClick={() => setStatusTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="pending-services-empty">
            <Empty description="Loading services…" />
          </div>
        ) : tableData.length ? (
          <Table
            variant="small"
            header={tableHeaders}
            data={tableData}
            aligncss="dashboard-table"
            actions={statusTab === "closed" ? [] : ["edit"]}
            restrictActionIcon
            onActionClick={(action, rowData) => {
              if (action !== "edit") return;
              if (!rowData?.client_id) return;

              if (statusTab !== "ongoing") return;

              if (
                String(rowData?.status || "")
                  .toLowerCase()
                  .trim() === SERVICE_COMPLETED_STATUS.toLowerCase()
              ) {
                return;
              }

              if (!rowData?.client_service_id) return;
              setEditClientId(String(rowData.client_id));
              setEditServiceId(String(rowData.client_service_id));
              setEditInitialValues({
                start_date: rowData?.start_date,
                service_name: rowData?.service_name,
                tax_year: rowData?.tax_year,
                service_frequency: rowData?.service_frequency,
                client_business_id: rowData?.client_business_id,
                status: normalizeServiceStatus(rowData?.status),
              });
              setEditModalOpen(true);
            }}
            hiddenFields={["client_id", "category_key", "client_service_id", "client_business_id"]}
            onRowClick={(row, event) => {
              // If user clicked the Action column, Table may still bubble the click;
              // ignore such clicks so only non-action row clicks navigate.
              const inAction =
                event?.target?.closest?.(".row-action") ||
                event?.target?.closest?.(".row-action-more");
              if (inAction) {
                return;
              }

              if (!row?.client_id) return;
              const category = normalizeClientDetailCategory(
                row?.category_key || row?.service_category || categoryTab,
              );
              navigateToClientDetails(navigate, row.client_id, {
                state: {
                  tab: "services",
                  documentCategory: category,
                  service_category: category,
                  service_status_tab:
                    statusTab === "closed" ? "closed" : "ongoing",
                },
              });
            }}
          />
        ) : (
          <div className="pending-services-empty">
            <Empty description={emptyLabel} />
          </div>
        )}
      </div>

      {editModalOpen && editInitialValues && (
        <Modal
          title={null}
          open={editModalOpen}
          handleClose={() => {
            if (editSubmitting) return;
            resetEditState();
          }}
          className="client-services-section__modal timesheet-view-modal"
        >
          <div className="my-services-modal-content">
            <div className="my-services-modal-header">
              <h3>Edit {categoryLabel.toLowerCase()} service</h3>
              <button
                type="button"
                className="my-services-modal-close"
                onClick={() => !editSubmitting && resetEditState()}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <ClientServiceRequestForm
              defaultCategory={normalizedCategory}
              lockCategory
              clientId={editClientId}
              submitting={editSubmitting}
              submitLabel="Update service"
              showStatus
              initialValues={editInitialValues}
              onSubmit={async (form) => {
                if (!editClientId || !editServiceId) return;
                const nextStatus = normalizeServiceStatus(form.status);
                if (nextStatus === SERVICE_COMPLETED_STATUS) {
                  setCompleteForm(form);
                  return;
                }
                setEditSubmitting(true);
                try {
                  const res = await CLIENT_ACTION.updateClientService({
                    client_id: editClientId,
                    client_service_id: editServiceId,
                    start_date: form.start_date,
                    service_category: categoryTab,
                    service_name: form.service_name,
                    tax_year: form.tax_year,
                    service_frequency: form.service_frequency,
                    status: nextStatus,
                    ...(normalizedCategory === "business"
                      ? { client_business_id: form.client_business_id }
                      : {}),
                  });

                  if (res?.status === 1) {
                    Toaster.success(
                      res.message || "Service updated successfully",
                    );
                    resetEditState();
                    if (nextStatus === SERVICE_COMPLETED_STATUS) {
                      if (statusTab === "closed") {
                        await loadServices("closed", categoryTab);
                      } else {
                        setStatusTab("closed");
                      }
                    } else {
                      await loadServices(statusTab, categoryTab);
                    }
                  } else {
                    Toaster.error(res?.message || "Could not update service");
                  }
                } finally {
                  setEditSubmitting(false);
                }
              }}
              onCancel={() => resetEditState()}
            />
          </div>
        </Modal>
      )}

      <CompleteServiceFiledCopiesModal
        open={Boolean(completeForm)}
        clientId={editClientId}
        clientServiceId={editServiceId}
        serviceCategory={normalizedCategory}
        clientBusinessId={completeForm?.client_business_id}
        submitting={editSubmitting}
        onClose={() => {
          if (editSubmitting) return;
          setCompleteForm(null);
        }}
        onSubmit={async (documentIds) => {
          if (!editClientId || !editServiceId || !completeForm) return;
          setEditSubmitting(true);
          try {
            const res = await CLIENT_ACTION.updateClientService({
              client_id: editClientId,
              client_service_id: editServiceId,
              start_date: completeForm.start_date,
              service_category: categoryTab,
              service_name: completeForm.service_name,
              tax_year: completeForm.tax_year,
              service_frequency: completeForm.service_frequency,
              status: SERVICE_COMPLETED_STATUS,
              document_ids: documentIds,
              ...(normalizedCategory === "business"
                ? { client_business_id: completeForm.client_business_id }
                : {}),
            });
            if (res?.status === 1) {
              Toaster.success(res.message || "Service updated successfully");
              const nextCategory = categoryTab;
              resetEditState();
              if (statusTab === "closed") {
                await loadServices("closed", nextCategory);
              } else {
                setStatusTab("closed");
              }
            } else {
              Toaster.error(res?.message || "Could not update service");
            }
          } finally {
            setEditSubmitting(false);
          }
        }}
      />
    </div>
  );
};

export default ServicesPage;
